import { Exam, ParticipantAnswers, Question, QuestionAnswer, ExamResult as QuestionnaireResult, QuestionStatus, Survey } from '@core/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface QuestionnaireState {
    type: 'survey' | 'exam' | null;
    status: QuestionStatus;
    title?: string;
    intro?: string;
    outro?: string;
    passed?: string;
    failed?: string;
    required?: boolean;
    questions?: Question[];
    questionIndex?: number;
    question?: Question;
    answer?: QuestionAnswer;
    answers?: ParticipantAnswers;
    showIsRequired?: boolean;
    questionnaireResult?: QuestionnaireResult;
}

const initialState: QuestionnaireState = {
    type: null,
    status: QuestionStatus.Closed
};

const hasAnswer = (answer: QuestionAnswer): boolean => {
    if (answer.openAnswer) {
        return true;
    }

    if (answer.selectedAnswers && answer.selectedAnswers.length > 0) {
        return true;
    }

    return false;
};

export const questionnaireSlice = createSlice({
    name: 'questionnaire',
    initialState,
    reducers: {
        previousQuestion: (state, payload: PayloadAction<QuestionAnswer | null>) => {
            if (!state.questions) {
                return;
            }

            if (!state.answers) {
                state.answers = { isFinalized: false, answers: [] };
            }

            if (state.status === QuestionStatus.ShowQuestion && payload.payload) {
                state.answers.answers[state.questionIndex || 0] = payload.payload;
            }

            state.questionIndex = (state.questionIndex || 0) - 1;
            if (state.questionIndex < 0) {
                state.questionIndex = 0;
                state.status = QuestionStatus.ShowIntro;
            }

            if (state.status === QuestionStatus.ShowOutro) {
                state.status = QuestionStatus.ShowQuestion;
            }

            state.showIsRequired = false;
            state.question = state.questions[state.questionIndex];
            if (!state.answers.answers[state.questionIndex]) {
                state.answers.answers[state.questionIndex] = { questionId: state.question.questionId };
            }
            state.answer = state.answers.answers[state.questionIndex];
        },
        nextQuestion: (state, payload: PayloadAction<QuestionAnswer | null>) => {
            if (!state.questions) {
                return;
            }

            if (state.status === QuestionStatus.ShowQuestion && state.question?.required === true && (!payload.payload || !hasAnswer(payload.payload))) {
                // Don't allow to go to next question if current question is required and no answer was given
                state.showIsRequired = true;
                return;
            }

            if (!state.answers) {
                state.answers = { isFinalized: false, answers: [] };
            }

            if (state.status === QuestionStatus.ShowQuestion) {
                if (payload.payload) {
                    state.answers.answers[state.questionIndex || 0] = payload.payload;
                } else {
                    state.answers.answers[state.questionIndex || 0] = { questionId: state.question?.questionId || '' };
                }
            }

            state.showIsRequired = false;
            if (state.status === QuestionStatus.ShowIntro) {
                state.status = QuestionStatus.ShowQuestion;
                state.questionIndex = 0;
            } else if (state.status === QuestionStatus.ShowQuestion) {
                state.questionIndex = (state.questionIndex || 0) + 1;
                if (state.questionIndex >= state.questions.length) {
                    state.status = QuestionStatus.ShowOutro;
                    return;
                }
            } else if (state.status === QuestionStatus.ShowOutro) {
                return;
            }

            state.question = state.questions[state.questionIndex || 0];
            if (!state.answers.answers[state.questionIndex || 0]) {
                state.answers.answers[state.questionIndex || 0] = { questionId: state.question?.questionId || '' };
            }
            state.answer = state.answers.answers[state.questionIndex || 0];
        },
        stopQuestionnaire: (state) => {
            state.questions = undefined;
            state.status = QuestionStatus.Closed;
        },
        startQuestionnaire: (state, action: PayloadAction<{ type: 'survey' | 'exam'; questionnaire: Survey | Exam; answers: ParticipantAnswers | null }>) => {
            if (action.payload.questionnaire.questions.length === 0 || !action.payload.questionnaire.enabled || !action.payload.questionnaire.isValid) {
                // Ignore surveys with no questions, disabled or invalid
                return;
            }
            state.type = action.payload.type;
            state.title = action.payload.questionnaire.title;
            state.intro = action.payload.questionnaire.intro;
            state.outro = action.payload.questionnaire.outro;
            state.required = action.payload.questionnaire.required;
            state.questions = action.payload.questionnaire.questions;
            state.status = QuestionStatus.ShowIntro;
            state.answers = action.payload.answers ?? {
                isFinalized: false,
                answers: []
            };
            if (state.answers.isFinalized) {
                state.status = QuestionStatus.ShowOutro;
            }
            if (state.type === 'exam') {
                state.passed = (action.payload.questionnaire as Exam).passed || '';
                state.failed = (action.payload.questionnaire as Exam).failed || '';
            }
            state.questionnaireResult = undefined;
        },
        questionnaireResultUpdated: (state, action: PayloadAction<QuestionnaireResult>) => {
            state.questionnaireResult = action.payload;
            if (state.answers) {
                state.answers.isFinalized = state.type === 'exam';
            }
        },
        refreshQuestionnaireResult: (state) => {},
        retryQuestionnaire: (state) => {
            if (!state.answers) {
                return;
            }

            if (!state.answers.isFinalized) {
                return;
            }

            state.status = QuestionStatus.ShowIntro;
            state.questionnaireResult = undefined;
            state.answers.isFinalized = false;
            state.answers.answers = [];
        }
    }
});

export const {
    previousQuestion,
    nextQuestion,
    stopQuestionnaire,
    startQuestionnaire,
    questionnaireResultUpdated,
    refreshQuestionnaireResult,
    retryQuestionnaire
} = questionnaireSlice.actions;

export default questionnaireSlice.reducer;
